import React, { useState } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { getAllImageUrls } from "../../utils/imageHelper";

const CardImageGallery = ({ imageSrc, altTitle = "Image", className = "h-64 md:h-72" }) => {
  const images = getAllImageUrls(imageSrc);
  const [activeIdx, setActiveIdx] = useState(0);

  if (images.length === 0) {
    return (
      <div className={`w-full bg-slate-100 flex items-center justify-center ${className}`}>
        <span className="text-slate-400 font-bold text-xs uppercase tracking-widest">No Image</span>
      </div>
    );
  }

  const currentImage = images[activeIdx] || images[0];

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className={`w-full relative overflow-hidden bg-slate-950 group/gallery ${className}`}>
      {/* Ambient Blurred background image to eliminate stark side bars */}
      <img
        src={currentImage}
        alt=""
        className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-40 select-none pointer-events-none"
      />

      {/* Main Image displayed 100% uncropped */}
      <img
        src={currentImage}
        alt={`${altTitle} ${activeIdx + 1}`}
        className="relative z-10 w-full h-full object-contain p-2 transition-all duration-500 group-hover/gallery:scale-105"
      />

      {/* Gradient overlay for contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 opacity-60 group-hover/gallery:opacity-80 transition-opacity pointer-events-none z-10" />

      {/* Navigation Arrows for Multi-image */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-white text-white hover:text-slate-900 flex items-center justify-center backdrop-blur-md transition-all opacity-0 group-hover/gallery:opacity-100 shadow-md border border-white/20 z-20"
          >
            <HiChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-white text-white hover:text-slate-900 flex items-center justify-center backdrop-blur-md transition-all opacity-0 group-hover/gallery:opacity-100 shadow-md border border-white/20 z-20"
          >
            <HiChevronRight size={20} />
          </button>
        </>
      )}

      {/* Thumbnails Bar */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2 px-4 z-20">
          {images.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveIdx(idx);
              }}
              className={`relative rounded-lg overflow-hidden transition-all duration-300 ${
                activeIdx === idx
                  ? "w-9 h-9 ring-2 ring-blue-500 scale-110 shadow-lg z-10"
                  : "w-7 h-7 opacity-75 hover:opacity-100 hover:scale-105 border border-white/40"
              }`}
            >
              <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Image counter tag */}
      {images.length > 1 && (
        <div className="absolute top-4 right-4 z-20">
          <span className="px-2.5 py-1 bg-black/50 backdrop-blur-md border border-white/20 text-[10px] font-black text-white rounded-full shadow-sm">
            {activeIdx + 1} / {images.length}
          </span>
        </div>
      )}
    </div>
  );
};

export default CardImageGallery;
